import { el, clear } from './dom.js';

/**
 * 写真での内見。
 * 3Dスキャンが用意できていない物件でも、部屋ごとに写真を切り替えて見せる。
 * 間取り図・AI内見・物件切り替えは3Dのときと同じものを使う。
 */
export function createPhotoViewer(container, { property, onRoomChange }) {
  const image = el('img', {
    class: 'pv-img',
    alt: '',
    decoding: 'async',
  });
  const caption = el('p', { class: 'pv-caption' }, '');
  const counter = el('span', { class: 'pv-counter' }, '');

  const prevBtn = el('button', { class: 'pv-nav pv-prev', type: 'button', 'aria-label': '前の写真' }, '‹');
  const nextBtn = el('button', { class: 'pv-nav pv-next', type: 'button', 'aria-label': '次の写真' }, '›');
  const thumbs = el('div', { class: 'pv-thumbs' });

  const stage = el('div', { class: 'pv-stage' }, [image, prevBtn, nextBtn, counter]);
  const root = el('div', { class: 'pv' }, [stage, caption, thumbs]);
  container.appendChild(root);

  /** いま表示している部屋の写真（元データは書き換えない） */
  let shots = [];
  let index = 0;
  let room = '';

  function showRoom(nextRoom) {
    room = nextRoom;
    shots = property.photos.filter((p) => p.room === nextRoom);
    index = 0;
    renderThumbs();
    render();
    if (typeof onRoomChange === 'function') onRoomChange(nextRoom);
  }

  function step(delta) {
    if (shots.length === 0) return;
    // 端で止めず巡回させる。部屋ごとの枚数が少ないため行き止まり感が出る
    index = (index + delta + shots.length) % shots.length;
    render();
  }

  function render() {
    const shot = shots[index];
    if (shot === undefined) {
      image.removeAttribute('src');
      caption.textContent = 'この部屋の写真はまだありません';
      counter.textContent = '';
      return;
    }
    image.src = shot.src;
    image.alt = `${room}${shot.caption ? ` ${shot.caption}` : ''}`;
    caption.textContent = shot.caption || room;
    counter.textContent = `${index + 1} / ${shots.length}`;

    for (const [i, btn] of [...thumbs.children].entries()) {
      btn.classList.toggle('is-active', i === index);
    }

    const many = shots.length > 1;
    prevBtn.hidden = !many;
    nextBtn.hidden = !many;
  }

  function renderThumbs() {
    clear(thumbs);
    shots.forEach((shot, i) => {
      const img = el('img', { src: shot.src, alt: '', loading: 'lazy', decoding: 'async' });
      const btn = el(
        'button',
        {
          class: 'pv-thumb',
          type: 'button',
          'aria-label': shot.caption || `${room} ${i + 1}枚目`,
          onClick: () => {
            index = i;
            render();
          },
        },
        img,
      );
      thumbs.appendChild(btn);
    });
  }

  prevBtn.addEventListener('click', () => step(-1));
  nextBtn.addEventListener('click', () => step(1));

  // 左右キーとスワイプでも送れるようにする
  const onKey = (event) => {
    if (event.key === 'ArrowLeft') step(-1);
    else if (event.key === 'ArrowRight') step(1);
  };
  window.addEventListener('keydown', onKey);

  let swipeFrom = null;
  stage.addEventListener('pointerdown', (e) => {
    swipeFrom = { x: e.clientX, y: e.clientY };
  }, { passive: true });
  stage.addEventListener('pointerup', (e) => {
    if (swipeFrom === null) return;
    const dx = e.clientX - swipeFrom.x;
    const dy = e.clientY - swipeFrom.y;
    swipeFrom = null;
    // 縦方向の動きが大きいときはスクロール操作とみなして無視する
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
  }, { passive: true });

  return {
    showRoom,
    get room() {
      return room;
    },
    dispose() {
      window.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
}
